import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { Image } from '@/atoms/Image'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { HeadingLG, HeadingSM, HeadingXL, ParagraphSM, Text, TitleLG, TitleSM } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { BasicCarousel } from '@/molecules/BasicCarousel'
import { getWebClient } from '@/services/ApolloClient'
import { GET_AVAILABLE_COLLECTIBLE_OFFERS_BY_PROJECT } from '@/services/ProjectsService/queries'
import { CollectibleOffer, GetAvailableCollectibleOffersByProjectQuery } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { logger } from '@/utils/logging'
import { CollectiblesProductCarouselItem } from './CollectibleProductCarouselItem'

export const CollectiblesView = () => {
  const { locale } = useLocale()
  const client = getWebClient({ locale })
  const [offers, setOffers] = useState<CollectibleOffer[]>([])

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const fetchedOffers = await client.query<GetAvailableCollectibleOffersByProjectQuery>({
          query: GET_AVAILABLE_COLLECTIBLE_OFFERS_BY_PROJECT,
          variables: { input: { projectId: '' } },
          errorPolicy: 'all',
        })
        setOffers(fetchedOffers.data?.availableCollectibleOffers?.filter((o): o is CollectibleOffer => o != null) ?? [])
      } catch (error) {
        logger().warn(`Error fetching collectible offers for Collectibles View`, { error })
      }
    }

    fetchOffers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const theatricalOffers = [slugs.sight, slugs.soundOfHopeTheStoryOfPossumTrot]

  const showOffers = (offers as CollectibleOffer[])?.filter(
    (offer) =>
      offer?.projectId &&
      !theatricalOffers.includes(offer.projectId) &&
      offer?.products?.some(
        (product) =>
          product?.__typename === 'CollectibleProductPack' || product?.__typename === 'CollectibleProductMoment',
      ),
  ) as CollectibleOffer[]

  const groupedOffers = groupByProjectId(showOffers)

  return (
    <div>
      <PaddedContainer>
        <section>
          <div className="mb-5 flex flex-wrap items-end justify-between">
            <HeadingXL>Digital Collectibles</HeadingXL>
            <TitleLG weight="semibold" className="max-w-80">
              Fund your favorite shows and collect scenes
            </TitleLG>
          </div>
          <BasicCarousel>
            {Object.keys(groupedOffers).map((projectId) => {
              const projectOffers = groupedOffers[projectId]
              return <CollectiblesProductCarouselItem key={projectId} slug={projectId} offers={projectOffers} />
            })}
          </BasicCarousel>
        </section>
        <div className="my-16 text-center">
          <HeadingLG>How Digital Collectibles Work</HeadingLG>
          <ParagraphSM>Unique scenes from your favorite show</ParagraphSM>
        </div>
        <section>
          <DetailsContainer
            number={1}
            title="Buy collectibles pack"
            body="Buy a pack with randomly selected frames from Seasons 1-2."
            image="/v1715117248/collectibles/packs/1200-WFS_S1_2PK.webp"
          />
          <DetailsContainer
            number={2}
            title="Reveal your scenes"
            body="Open your digital collectibles and enjoy scenes from shows you love."
            image="/v1716495438/collectibles/collectibles-grid-preview.webp"
            reverse
          />
          <DetailsContainer
            number={3}
            title="Enjoy!"
            body="Your frames can now be shared, downloaded, or gifted."
            image="/v1716495519/collectibles/wingfeather-family-prayer.webp"
          />
        </section>
        <HeadingLG className="my-16 text-center">Optional Upgrades</HeadingLG>
        <section>
          <div className="mx-auto my-6 max-w-4xl rounded-2xl bg-white p-6 text-center md:px-12 md:py-14">
            <TitleSM weight="semibold" className="mx-auto">
              Dedicated your digitial collectible
            </TitleSM>
            <ParagraphSM className="mx-auto my-4 md:w-2/3">
              Add a note to a loved one, or why you love the frame, and it will be included on your print. Visible in
              your account and shareable.
            </ParagraphSM>
            <Image
              src="/v1716493064/collectibles/dedication_preview.webp"
              alt=""
              width={370}
              height={200}
              className="mx-auto md:hidden"
            />
            <Image
              src="/v1716493064/collectibles/dedication_preview.webp"
              alt=""
              width={650}
              height={400}
              className="mx-auto hidden md:block"
            />
          </div>
          <div className="mx-auto my-6 max-w-4xl rounded-2xl bg-white p-6 text-center md:px-12 md:py-14">
            <TitleSM weight="semibold" className="mx-auto">
              Request a signature
            </TitleSM>
            <ParagraphSM className="mx-auto my-4 md:w-2/3">
              Request signatures from talent who helped create your favorite shows.
            </ParagraphSM>
            <Image
              src="/v1716494717/collectibles/signature_demo.webp"
              alt=""
              width={370}
              height={200}
              className="mx-auto rounded-xl md:hidden"
            />
            <Image
              src="/v1716494717/collectibles/signature_demo.webp"
              alt=""
              width={650}
              height={400}
              className="mx-auto hidden rounded-xl md:block"
            />
          </div>
          <DetailsContainer
            title="Buy printed keepsake"
            body="For an additional cost, you can also purchase a printed keepsake of your favorite frame."
            image="/v1713892031/collectibles/wingfeather-saga/wingfeather-fan-3.webp"
          />
        </section>
      </PaddedContainer>
    </div>
  )
}

function groupByProjectId(showOffers: CollectibleOffer[]) {
  return showOffers.reduce((result, offer) => {
    const { projectId } = offer
    if (projectId) {
      if (!result[projectId]) {
        result[projectId] = []
      }
      result[projectId]?.push(offer)
    }
    return result
  }, {} as Record<string, CollectibleOffer[]>)
}

interface DetailsContainerProps {
  title: string
  body: string
  image: string
  number?: number
  reverse?: boolean
}

const DetailsContainer = ({ number, title, body, image, reverse }: DetailsContainerProps) => (
  <div
    className={classNames(
      'mx-auto my-6 flex max-w-4xl items-center gap-5 rounded-2xl bg-white px-6  py-8 justify-between flex-wrap md:px-12 md:py-14',
      reverse && 'flex-row-reverse',
    )}
  >
    <div className={classNames('flex flex-col gap-3 w-[350px]', reverse && ' lg:ml-16')}>
      {number && (
        <Text
          className="photon-title-lg flex h-12 w-12 items-center justify-center rounded-full border border-core-gray-600 text-center"
          color="core-gray-600"
        >
          {number}
        </Text>
      )}
      <HeadingSM weight="semibold">{title}</HeadingSM>
      <ParagraphSM>{body}</ParagraphSM>
    </div>
    <Image src={image} alt="" width={350} height={200} className="rounded-lg" />
  </div>
)
